import React from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Heading,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { ArrowBackIcon, CheckIcon, ExternalLinkIcon, RepeatIcon } from "@chakra-ui/icons";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { Link } from "react-router-dom";
import { api } from "../api";

const domainLabels = {
  clinical: "Clinical",
  operational: "Operational",
  billing: "Billing",
  medical_director: "Medical Director",
};

function formatName(user) {
  return [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || user?.id || "";
}

function crewRows(chart) {
  const rows = [
    {
      index: 0,
      slot: "Primary caregiver",
      name: chart?.primaryCaregiverName,
      externalId: chart?.primaryCaregiverExternalId,
      linkedUser: chart?.primaryCaregiverUser,
      matchStatus: chart?.primaryCaregiverUser?.id ? "linked" : "unmatched",
      candidateUsers: chart?.visibilityPolicy?.crewLinkSummary?.[0]?.candidateUsers || [],
    },
    {
      index: 1,
      slot: "Secondary crew",
      name: chart?.secondaryCrewMemberName,
      externalId: chart?.secondaryCrewMemberExternalId,
      linkedUser: chart?.secondaryCrewMemberUser,
      matchStatus: chart?.secondaryCrewMemberUser?.id ? "linked" : "unmatched",
      candidateUsers: chart?.visibilityPolicy?.crewLinkSummary?.[1]?.candidateUsers || [],
    },
    {
      index: 2,
      slot: "Third crew",
      name: chart?.thirdCrewMemberName,
      externalId: chart?.thirdCrewMemberExternalId,
      linkedUser: chart?.thirdCrewMemberUser,
      matchStatus: chart?.thirdCrewMemberUser?.id ? "linked" : "unmatched",
      candidateUsers: chart?.visibilityPolicy?.crewLinkSummary?.[2]?.candidateUsers || [],
    },
  ];
  const otherRows = Array.isArray(chart?.otherCrew)
    ? chart.otherCrew.map((entry, otherIndex) => ({
        index: otherIndex + 3,
        slot: `Other crew ${otherIndex + 1}`,
        name: entry?.name,
        externalId: entry?.externalId,
        linkedUser: entry?.linkedUserId ? { id: entry.linkedUserId } : null,
        matchStatus: entry?.matchStatus || "unmatched",
        matchBasis: entry?.matchBasis || null,
        candidateUsers: entry?.candidateUsers || [],
      }))
    : [];
  return [...rows, ...otherRows].filter((row) => row.name || row.externalId || row.linkedUser?.id || row.candidateUsers?.length);
}

function statusColor(value) {
  const token = String(value || "").toLowerCase();
  if (token.includes("fail")) return "red";
  if (token.includes("needs") || token.includes("pending") || token.includes("awaiting")) return "orange";
  if (token.includes("completed") || token.includes("released") || token.includes("acceptable")) return "green";
  return "gray";
}

function humanizeKey(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDisplayValue(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value).replace(/_/g, " ");
}

function toValidDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function firstDate(...values) {
  for (const value of values) {
    const date = toValidDate(value);
    if (date) return date;
  }
  return null;
}

function formatDateTime(value) {
  const date = toValidDate(value);
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function resolveSubmittedAt(chart) {
  const sourceMetadata = chart?.sourceMetadata || {};
  return firstDate(
    sourceMetadata.submittedAt,
    sourceMetadata.submissionAt,
    sourceMetadata.receivedAt,
    sourceMetadata.uploadedAt,
    sourceMetadata.ingestedAt,
    sourceMetadata.createdAt,
    chart?.createdAt
  );
}

function resolveAiCompletedAt(chart) {
  const sourceMetadata = chart?.sourceMetadata || {};
  const report = chart?.synthesizedReport || {};
  const confidenceSummary = chart?.confidenceSummary || {};
  return firstDate(
    confidenceSummary.evaluatedAt,
    confidenceSummary.completedAt,
    report.generatedAt,
    report.completedAt,
    sourceMetadata.evaluatedAt,
    sourceMetadata.aiCompletedAt,
    sourceMetadata.processedAt,
    chart?.updatedAt
  );
}

function ChartTimestamps({ chart, compact = false }) {
  const submittedAt = resolveSubmittedAt(chart);
  const aiCompletedAt = resolveAiCompletedAt(chart);
  return (
    <HStack spacing={compact ? 2 : 3} wrap="wrap" color={textMuted} fontSize="xs">
      <Text>Submitted {formatDateTime(submittedAt)}</Text>
      <Text>AI completed {formatDateTime(aiCompletedAt)}</Text>
    </HStack>
  );
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((entry) => entry !== null && entry !== undefined && entry !== "");
  return [value];
}

function DetailDisclosure({ title, count = null, children }) {
  return (
    <Box as="details" {...mutedPanelProps} p={3}>
      <Box as="summary" cursor="pointer" listStyleType="none">
        <Flex justify="space-between" align="center" gap={3}>
          <Text fontWeight="semibold">{title}</Text>
          {count !== null ? <Badge colorScheme="gray">{count}</Badge> : null}
        </Flex>
      </Box>
      <Box mt={3}>{children}</Box>
    </Box>
  );
}

function facetBadge(facet, fallbackValue = null) {
  const value = facet?.value || fallbackValue;
  if (!facet && !value) return null;
  return (
    <Badge colorScheme={facet?.colorScheme || statusColor(value)}>
      {facet?.label || humanizeKey(value)}
    </Badge>
  );
}

function PresentationBadges({ chart, includeHuman = false, includeReview = true }) {
  const presentation = chart?.reviewPresentation || {};
  return (
    <HStack spacing={2} wrap="wrap">
      {facetBadge(presentation.processingState, chart?.status)}
      {includeReview ? facetBadge(presentation.reviewState, chart?.reviewStatus) : null}
      {facetBadge(presentation.aiOutcome, chart?.confidenceSummary?.qualityTier || chart?.confidenceSummary?.overallStatus)}
      {includeHuman ? facetBadge(presentation.humanOutcome) : null}
      {includeHuman ? facetBadge(presentation.trainingState) : null}
    </HStack>
  );
}

function ReviewMeaning({ chart }) {
  const quickQa = chart?.canonicalRecord?.quickQa || {};
  const manualReviewRequired =
    chart?.confidenceSummary?.manualReviewRequired === true ||
    quickQa?.chart_review?.manual_review_required === true ||
    chart?.reviewStatus === "needs_review";
  const releaseStates = Object.values(chart?.clinicianReleaseState || {});
  const heldByReleasePolicy = releaseStates.some((state) => state?.visibleToClinician === false || String(state?.reviewStatus || "").includes("pending"));
  const routingSources = [
    quickQa?.chart_review?.manual_review_required === true ? "AI chart review flag" : null,
    chart?.confidenceSummary?.manualReviewRequired === true ? "AI confidence summary flag" : null,
    chart?.reviewStatus === "needs_review" ? "chart review status" : null,
    heldByReleasePolicy ? "workflow domain release policy" : null,
  ].filter(Boolean);
  const reasons = [
    ...normalizeList(quickQa?.chart_review?.manual_review_reasons),
    ...normalizeList(chart?.confidenceSummary?.manualReviewReasons),
    ...normalizeList(chart?.synthesizedReport?.manualReviewReasons),
  ];

  return (
    <Box {...mutedPanelProps} p={3}>
      <Stack spacing={2}>
        <Flex justify="space-between" gap={3} align="flex-start">
          <Box>
            <Text fontWeight="semibold">Review Routing</Text>
            <Text fontSize="sm" color={textMuted}>
              {manualReviewRequired
                ? "Queued for human QA before release. This is not a completed human decision."
                : heldByReleasePolicy
                  ? "The AI did not flag manual review, but workflow release policy still holds domains until reviewer release."
                  : "The AI did not flag manual review and no reviewer hold is recorded."}
            </Text>
          </Box>
          <Badge colorScheme={manualReviewRequired ? "orange" : "green"}>
            {manualReviewRequired ? "queued for human QA" : "AI complete"}
          </Badge>
        </Flex>
        {(routingSources.length > 0 || reasons.length > 0) && (
          <Box as="details">
            <Box as="summary" cursor="pointer" color={textMuted} fontSize="sm">
              Why this is queued
            </Box>
            {routingSources.length > 0 ? (
              <Text mt={2} fontSize="sm" color={textMuted}>
                Source: {routingSources.join(", ")}
              </Text>
            ) : null}
            {reasons.map((reason, index) => (
              <Text key={`review-reason-${index}`} mt={1} fontSize="sm" color="orange.100">
                - {formatDisplayValue(reason)}
              </Text>
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function AiMetric({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box {...mutedPanelProps} p={3}>
      <Text fontSize="xs" color={textMuted} textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Badge mt={1} colorScheme={statusColor(value)}>{formatDisplayValue(value)}</Badge>
    </Box>
  );
}

function DomainReviewCard({ title, review }) {
  if (!review || typeof review !== "object") return null;
  const details = Object.entries(review).filter(([key, value]) => {
    if (["status", "applicability"].includes(key)) return false;
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== "";
  });

  return (
    <Box {...mutedPanelProps} p={3}>
      <Flex justify="space-between" gap={3} align="flex-start" mb={2}>
        <Text fontWeight="semibold">{title}</Text>
        <Badge colorScheme={statusColor(review.status || review.applicability)}>
          {formatDisplayValue(review.status || review.applicability || "reviewed")}
        </Badge>
      </Flex>
      <Stack spacing={2}>
        {details.slice(0, 5).map(([key, value]) => (
          <Box key={key}>
            <Text fontSize="xs" color={textMuted} textTransform="uppercase" letterSpacing="0.04em">
              {humanizeKey(key)}
            </Text>
            {Array.isArray(value) ? (
              <Stack spacing={1} mt={1}>
                {value.slice(0, 4).map((item, index) => (
                  <Text key={`${key}-${index}`} fontSize="sm" whiteSpace="pre-wrap">
                    - {formatDisplayValue(item)}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text fontSize="sm" whiteSpace="pre-wrap">{formatDisplayValue(value)}</Text>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function ReportListSection({ title, items }) {
  const values = normalizeList(items);
  if (!values.length) return null;
  return (
    <Box {...mutedPanelProps} p={3}>
      <Heading size="xs" mb={2}>{title}</Heading>
      <Stack spacing={1}>
        {values.slice(0, 6).map((item, index) => (
          <Text key={`${title}-${index}`} fontSize="sm" whiteSpace="pre-wrap">
            - {formatDisplayValue(item)}
          </Text>
        ))}
      </Stack>
    </Box>
  );
}

function LiveQaReportDetails({ report }) {
  const patientFeedback = normalizeList(report?.per_patient_feedback || report?.student_report?.per_patient_feedback);
  const hasDetails = Boolean(
    normalizeList(report?.strengths || report?.student_report?.strengths).length ||
    normalizeList(report?.growth_opportunities || report?.student_report?.growth_opportunities).length ||
    normalizeList(report?.clinical_quality_notes || report?.student_report?.clinical_quality_notes).length ||
    normalizeList(report?.pattern_warnings || report?.student_report?.pattern_warnings).length ||
    patientFeedback.length ||
    report?.billing_feedback ||
    report?.compliance_feedback
  );
  if (!hasDetails) return null;

  return (
    <DetailDisclosure title="Additional AI Findings">
      <Stack spacing={3}>
        <Grid templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} gap={3}>
          <ReportListSection title="Strengths" items={report?.strengths || report?.student_report?.strengths} />
          <ReportListSection title="Growth Opportunities" items={report?.growth_opportunities || report?.student_report?.growth_opportunities} />
          <ReportListSection title="Clinical Notes" items={report?.clinical_quality_notes || report?.student_report?.clinical_quality_notes} />
          <ReportListSection title="Pattern Warnings" items={report?.pattern_warnings || report?.student_report?.pattern_warnings} />
        </Grid>
        {patientFeedback.length > 0 && (
          <Box {...mutedPanelProps} p={3}>
            <Heading size="xs" mb={2}>Patient Feedback</Heading>
            <Stack spacing={3}>
              {patientFeedback.slice(0, 4).map((patient, index) => (
                <Box key={`patient-feedback-${index}`} borderTopWidth={index ? "1px" : "0"} borderColor={borderColor} pt={index ? 3 : 0}>
                  <Flex justify="space-between" gap={3} align="flex-start">
                    <Text fontWeight="semibold">Patient {patient?.patient_id || index + 1}</Text>
                    {patient?.quality_assessment?.level ? (
                      <Badge colorScheme={statusColor(patient.quality_assessment.level)}>{formatDisplayValue(patient.quality_assessment.level)}</Badge>
                    ) : null}
                  </Flex>
                  <Text fontSize="sm" color={textMuted} whiteSpace="pre-wrap">
                    {formatDisplayValue(patient?.clinical_summary || patient?.documentation_feedback || patient?.clinical_reasoning_feedback)}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        <Grid templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} gap={3}>
          {report?.billing_feedback ? <DomainReviewCard title="Billing Feedback" review={report.billing_feedback} /> : null}
          {report?.compliance_feedback ? <DomainReviewCard title="Compliance Feedback" review={report.compliance_feedback} /> : null}
        </Grid>
      </Stack>
    </DetailDisclosure>
  );
}

function TrendsBetaPanel({ trends }) {
  if (!trends) return null;
  const domains = trends.domains && typeof trends.domains === "object" ? trends.domains : {};

  return (
    <Box {...mutedPanelProps} p={3} borderColor="purple.500">
      <Flex justify="space-between" gap={3} align="flex-start" mb={3}>
        <Box>
          <Heading size="xs">Trends Beta</Heading>
          <Text fontSize="sm" color={textMuted}>Reviewer-only longitudinal profile for this primary caregiver.</Text>
        </Box>
        <Badge colorScheme={trends.status === "beta_unavailable" ? "orange" : "purple"}>{trends.status || "available"}</Badge>
      </Flex>
      {trends.status === "beta_unavailable" ? (
        <Text color="orange.100">{trends.message || "This feature is under development."}</Text>
      ) : (
        <Stack spacing={3}>
          {Object.entries(domains).map(([domain, detail]) => (
            <Box key={domain} {...mutedPanelProps} p={3}>
              <Text fontWeight="semibold">{domainLabels[domain] || humanizeKey(domain)}</Text>
              {detail?.summary ? <Text fontSize="sm" color={textMuted} mt={1}>{detail.summary}</Text> : null}
              {detail?.activeIssues?.length > 0 ? (
                <Stack spacing={1} mt={2}>
                  {detail.activeIssues.slice(0, 4).map((issue, index) => (
                    <Flex key={`${domain}-issue-${index}`} justify="space-between" gap={3}>
                      <Text fontSize="sm">{issue.label || issue.issueKey}</Text>
                      <Badge colorScheme={Number(issue.frequency || 0) > 1 ? "orange" : "gray"}>{issue.frequency || 1}x</Badge>
                    </Flex>
                  ))}
                </Stack>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function AiEvaluationReport({ chart }) {
  const quickQa = chart?.canonicalRecord?.quickQa || {};
  const report = chart?.synthesizedReport || {};
  const chartReview = quickQa?.chart_review || {};
  const domainReviews = quickQa?.domain_reviews && typeof quickQa.domain_reviews === "object" ? quickQa.domain_reviews : {};
  const materialIssues = normalizeList(quickQa?.material_issues || report?.materialIssues || report?.material_issues);
  const protocolFindings = normalizeList(
    quickQa?.jurisdiction_protocol_check?.protocol_findings ||
      quickQa?.jurisdiction_protocol_check?.findings ||
      quickQa?.protocol_findings
  );
  const summary =
    chartReview.summary ||
    report.summary ||
    report.overall_feedback ||
    report.findingsSummary ||
    report.student_report?.overall_feedback ||
    report.student_report?.summary ||
    "No AI summary was stored for this chart.";
  const hasAiPayload = Boolean(
    Object.keys(quickQa).length ||
    Object.keys(report).length ||
    Object.keys(chart?.confidenceSummary || {}).length ||
    Object.keys(chart?.specialistResults || {}).length
  );

  return (
    <Box {...panelProps} p={4}>
      <Stack spacing={4}>
        <Flex justify="space-between" gap={3} align="flex-start">
          <Box>
            <Heading size="sm">AI Chart Analysis</Heading>
            <Text color={textMuted} fontSize="sm">Machine-generated findings. Human QA decisions are separate.</Text>
          </Box>
        </Flex>

        {!hasAiPayload && (
          <Box {...mutedPanelProps} p={3}>
            <Text color="orange.100">
              No AI evaluation payload was stored with this chart. That usually means ingestion created the QA chart record but evaluation did not finish or failed before persistence.
            </Text>
          </Box>
        )}

        <ReviewMeaning chart={chart} />

        <TrendsBetaPanel trends={report.trends} />

        <Box {...mutedPanelProps} p={3}>
          <Flex justify="space-between" gap={3} align="flex-start">
            <Box>
              <Text fontWeight="semibold">AI Summary</Text>
              <Text mt={1} whiteSpace="pre-wrap">{summary}</Text>
            </Box>
            {chartReview.overall_status || chart?.confidenceSummary?.overallStatus ? (
              <Badge colorScheme={statusColor(chartReview.overall_status || chart.confidenceSummary.overallStatus)}>
                {formatDisplayValue(chartReview.overall_status || chart.confidenceSummary.overallStatus)}
              </Badge>
            ) : null}
          </Flex>
        </Box>

        <LiveQaReportDetails report={report} />

        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
          <AiMetric label="Quality" value={chartReview.quality_tier || chart?.confidenceSummary?.qualityTier} />
          <AiMetric label="Defensible" value={chartReview.defensible} />
          <AiMetric label="Coherent" value={chartReview.coherent} />
          <AiMetric label="Billing Support" value={chartReview.billing_support} />
        </Grid>

        {materialIssues.length > 0 && (
          <Box {...mutedPanelProps} p={3}>
            <Flex justify="space-between" align="center" gap={3} mb={2}>
              <Heading size="xs">Material Issues</Heading>
              <Badge colorScheme="orange">{materialIssues.length}</Badge>
            </Flex>
            <Stack spacing={2} mb={3}>
              {materialIssues.slice(0, 3).map((issue, index) => (
                <Flex key={`material-summary-${index}`} justify="space-between" gap={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="semibold">{formatDisplayValue(issue?.issue || issue?.topic || issue)}</Text>
                  {issue?.severity ? <Badge colorScheme={statusColor(issue.severity)}>{formatDisplayValue(issue.severity)}</Badge> : null}
                </Flex>
              ))}
            </Stack>
            <Box as="details">
              <Box as="summary" cursor="pointer" color={textMuted} fontSize="sm">
                View issue detail
              </Box>
              <Stack spacing={2} mt={3}>
                {materialIssues.slice(0, 8).map((issue, index) => (
                  <Box key={`material-${index}`} borderTopWidth={index ? "1px" : "0"} borderColor={borderColor} pt={index ? 2 : 0}>
                    <Flex justify="space-between" gap={3} align="flex-start">
                      <Text fontWeight="semibold">{formatDisplayValue(issue?.issue || issue?.topic || issue)}</Text>
                      {issue?.severity ? <Badge colorScheme={statusColor(issue.severity)}>{formatDisplayValue(issue.severity)}</Badge> : null}
                    </Flex>
                    {issue?.detail || issue?.finding || issue?.rationale ? (
                      <Text fontSize="sm" color={textMuted} whiteSpace="pre-wrap">
                        {formatDisplayValue(issue.detail || issue.finding || issue.rationale)}
                      </Text>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        )}

        {Object.keys(domainReviews).length > 0 && (
          <DetailDisclosure title="Domain-Level AI Findings" count={Object.keys(domainReviews).length}>
            <Grid templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} gap={3}>
              {Object.entries(domainReviews).map(([key, review]) => (
                <DomainReviewCard key={key} title={humanizeKey(key)} review={review} />
              ))}
            </Grid>
          </DetailDisclosure>
        )}

        {protocolFindings.length > 0 && (
          <DetailDisclosure title="Protocol / Jurisdiction Findings" count={protocolFindings.length}>
            <Stack spacing={2}>
              {protocolFindings.slice(0, 6).map((finding, index) => (
                <Box key={`protocol-${index}`}>
                  <Flex justify="space-between" gap={3} align="flex-start">
                    <Text fontWeight="semibold">{formatDisplayValue(finding?.topic || `Finding ${index + 1}`)}</Text>
                    {finding?.status ? <Badge colorScheme={statusColor(finding.status)}>{formatDisplayValue(finding.status)}</Badge> : null}
                  </Flex>
                  <Text fontSize="sm" color={textMuted} whiteSpace="pre-wrap">
                    {formatDisplayValue(finding?.finding || finding?.basis || finding)}
                  </Text>
                </Box>
              ))}
            </Stack>
          </DetailDisclosure>
        )}
      </Stack>
    </Box>
  );
}

function HumanReviewHistory({ decisions = [] }) {
  return (
    <Box {...panelProps} p={4}>
      <Heading size="sm" mb={3}>Human QA Decisions</Heading>
      {!decisions.length ? (
        <Text color={textMuted}>
          No reviewer decision has been recorded yet. Any review requirement above comes from AI routing signals or workflow release policy, not from a completed human QA decision.
        </Text>
      ) : (
        <Stack spacing={2}>
          {decisions.map((decision) => (
            <Box key={decision.id} {...mutedPanelProps} p={3}>
              <Flex justify="space-between" gap={3} align="flex-start">
                <Box>
                  <Text fontWeight="semibold">{domainLabels[decision.domain] || humanizeKey(decision.domain)}</Text>
                  <Text fontSize="sm" color={textMuted}>{decision.notes || "No notes entered."}</Text>
                </Box>
                <Badge colorScheme={statusColor(decision.decision)}>{formatDisplayValue(decision.decision)}</Badge>
              </Flex>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

const pageBg = "#07111f";
const panelBg = "#111827";
const panelBgMuted = "#0f172a";
const panelBgSelected = "#12304b";
const borderColor = "whiteAlpha.200";
const textMuted = "gray.300";

const panelProps = {
  borderWidth: "1px",
  borderColor,
  borderRadius: "lg",
  bg: panelBg,
  color: "gray.100",
  boxShadow: "0 18px 40px rgba(0,0,0,0.28)",
};

const mutedPanelProps = {
  borderWidth: "1px",
  borderColor,
  borderRadius: "md",
  bg: panelBgMuted,
  color: "gray.100",
};

const darkControlProps = {
  bg: panelBgMuted,
  color: "gray.100",
  borderColor: "whiteAlpha.300",
  _hover: { borderColor: "cyan.300" },
  _focusVisible: { borderColor: "cyan.300", boxShadow: "0 0 0 1px #67e8f9" },
  sx: {
    option: {
      background: panelBgMuted,
      color: "#f8fafc",
    },
  },
};

function ChartList({ charts, selectedChartId, onSelect }) {
  if (!charts.length) {
    return (
      <Box {...panelProps} p={4}>
        <Text color={textMuted}>No charts in this queue.</Text>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {charts.map((chart) => {
        const isSelected = String(selectedChartId || "") === String(chart.id || "");
        return (
          <Box
            key={chart.id || chart.sourceReference || chart.title}
            as="button"
            textAlign="left"
            borderWidth="1px"
            borderRadius="md"
            p={4}
            bg={isSelected ? panelBgSelected : panelBg}
            borderColor={isSelected ? "cyan.300" : borderColor}
            color="gray.100"
            cursor={chart.id ? "pointer" : "not-allowed"}
            opacity={chart.id ? 1 : 0.65}
            _hover={chart.id ? { borderColor: "cyan.300", bg: isSelected ? panelBgSelected : "#152033" } : undefined}
            onClick={() => chart.id && onSelect(chart)}
          >
            <Stack spacing={2}>
              <Flex justify="space-between" align="flex-start" gap={3}>
                <Heading size="sm">{chart.title}</Heading>
                {facetBadge(chart.reviewPresentation?.reviewState, chart.reviewStatus)}
              </Flex>
              <Text fontSize="sm" color={textMuted}>
                {chart.workflow?.name || "Agency QA"} - {chart.chartIdentifiers?.incidentNumber || chart.chartIdentifiers?.chartNumber || chart.sourceReference || chart.id}
              </Text>
              <ChartTimestamps chart={chart} compact />
              <PresentationBadges chart={chart} includeReview={false} />
              <HStack spacing={2} wrap="wrap">
                {(chart.domains || []).map((domain) => (
                  <Badge key={domain} variant="subtle">{domainLabels[domain] || domain}</Badge>
                ))}
              </HStack>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function CrewAlignment({ chart, canLink, onLinkCrew, onRetryLink }) {
  const rows = crewRows(chart);
  return (
    <Box {...panelProps} p={4}>
      <Flex justify="space-between" align="center" gap={3} mb={3}>
        <Heading size="sm">Crew Alignment</Heading>
        {canLink && (
          <Button size="sm" leftIcon={<RepeatIcon />} onClick={() => onRetryLink(chart.id)}>
            Retry Matching
          </Button>
        )}
      </Flex>
      {!rows.length ? (
        <Text color={textMuted}>No crew names were extracted for this chart.</Text>
      ) : (
        <Stack spacing={3}>
          {rows.map((row) => (
            <Box key={`${row.slot}:${row.index}`} {...mutedPanelProps} p={3}>
              <Flex justify="space-between" align="flex-start" gap={3}>
                <Box>
                  <Text fontWeight="semibold">{row.slot}</Text>
                  <Text fontSize="sm">{row.name || "No name extracted"}{row.externalId ? ` - ID ${row.externalId}` : ""}</Text>
                  {row.linkedUser?.id ? (
                    <Text fontSize="sm" color="green.200">Linked to {formatName(row.linkedUser) || row.linkedUser.id}</Text>
                  ) : (
                    <Text fontSize="sm" color="orange.200">{row.matchStatus === "candidate_review" ? "Needs reviewer alignment" : "Unlinked"}</Text>
                  )}
                </Box>
                <Badge colorScheme={row.linkedUser?.id ? "green" : row.candidateUsers?.length ? "orange" : "gray"}>
                  {row.linkedUser?.id ? "linked" : row.candidateUsers?.length ? "candidates" : "unmatched"}
                </Badge>
              </Flex>
              {canLink && !row.linkedUser?.id && row.candidateUsers?.length > 0 && (
                <Stack spacing={2} mt={3}>
                  {row.candidateUsers.map((candidate) => (
                    <Flex key={candidate.id} justify="space-between" align="center" gap={3} p={2} {...mutedPanelProps}>
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">{candidate.displayName || formatName(candidate)}</Text>
                        <Text fontSize="xs" color={textMuted}>{candidate.email} - score {candidate.score}</Text>
                      </Box>
                      <HStack>
                        <Button
                          size="xs"
                          onClick={() => onLinkCrew({ chartId: chart.id, row, candidate, persistAlias: false })}
                        >
                          Link
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          leftIcon={<CheckIcon />}
                          onClick={() => onLinkCrew({ chartId: chart.id, row, candidate, persistAlias: true })}
                        >
                          Link and Remember
                        </Button>
                      </HStack>
                    </Flex>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function ChartDetail({ payload, canLinkCrew, releasedView, onLinkCrew, onRetryLink, onSubmitResponse, responseDraft, setResponseDraft, saving, loadingDetail, detailError }) {
  const chart = payload?.chart;
  if (loadingDetail && !chart) {
    return (
      <Box {...panelProps} p={5}>
        <HStack spacing={3}>
          <Spinner size="sm" />
          <Text color={textMuted}>Loading case report...</Text>
        </HStack>
      </Box>
    );
  }

  if (!chart) {
    return (
      <Box {...panelProps} p={5}>
        <Text color={textMuted}>Select a chart to review.</Text>
      </Box>
    );
  }

  return (
    <Stack spacing={4}>
      <Box {...panelProps} p={5}>
        <Stack spacing={3}>
          <Flex justify="space-between" gap={3} align="flex-start">
            <Box>
              <Heading size="md">{chart.title}</Heading>
              <Text color={textMuted}>{chart.workflow?.name || "Agency QA"} - {chart.institution}</Text>
              <ChartTimestamps chart={chart} />
            </Box>
            {facetBadge(chart.reviewPresentation?.reviewState, chart.reviewStatus)}
          </Flex>
          {loadingDetail ? (
            <HStack color={textMuted} fontSize="sm">
              <Spinner size="xs" />
              <Text>Refreshing case report...</Text>
            </HStack>
          ) : null}
          {detailError ? (
            <Alert status="warning" borderRadius="md" bg="#3a2a10" color="orange.100" borderWidth="1px" borderColor="orange.700">
              <AlertIcon />
              <AlertDescription>{detailError}</AlertDescription>
            </Alert>
          ) : null}
          <PresentationBadges chart={chart} includeHuman includeReview={false} />
          <HStack spacing={2} wrap="wrap">
            {(chart.domains || []).map((domain) => (
              <Badge key={domain} colorScheme="blue">{domainLabels[domain] || domain}</Badge>
            ))}
          </HStack>
        </Stack>
      </Box>

      <AiEvaluationReport chart={chart} />

      <HumanReviewHistory decisions={payload?.reviewDecisions || []} />

      <CrewAlignment chart={chart} canLink={canLinkCrew} onLinkCrew={onLinkCrew} onRetryLink={onRetryLink} />

      <Box {...panelProps} p={4}>
        <Heading size="sm" mb={3}>Domain Release State</Heading>
        <Stack spacing={2}>
          {Object.entries(chart.clinicianReleaseState || {}).map(([domain, state]) => (
            <Flex key={domain} justify="space-between" gap={3} {...mutedPanelProps} p={3}>
              <Text fontWeight="semibold">{domainLabels[domain] || domain}</Text>
              <HStack>
                <Badge colorScheme={statusColor(state.reviewStatus)}>{state.reviewStatus || "pending"}</Badge>
                <Badge colorScheme={state.visibleToClinician ? "green" : "gray"}>{state.visibleToClinician ? "released" : "held"}</Badge>
              </HStack>
            </Flex>
          ))}
        </Stack>
      </Box>

      {releasedView && (
        <Box {...panelProps} p={4}>
          <Heading size="sm" mb={3}>Response</Heading>
          <Stack spacing={3}>
            <Select
              {...darkControlProps}
              value={responseDraft.domain}
              onChange={(event) => setResponseDraft((current) => ({ ...current, domain: event.target.value }))}
            >
              {(chart.domains || []).map((domain) => (
                <option key={domain} value={domain}>{domainLabels[domain] || domain}</option>
              ))}
            </Select>
            <Textarea
              {...darkControlProps}
              value={responseDraft.response}
              onChange={(event) => setResponseDraft((current) => ({ ...current, response: event.target.value }))}
              placeholder="Response or follow-up note"
            />
            <Textarea
              {...darkControlProps}
              value={responseDraft.addressedHow}
              onChange={(event) => setResponseDraft((current) => ({ ...current, addressedHow: event.target.value }))}
              placeholder="How this was addressed"
            />
            <Button colorScheme="blue" isLoading={saving} onClick={() => onSubmitResponse(chart.id)}>
              Submit Response
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export default function AgencyQaPortal() {
  const user = useUser(api);
  const [{ data: accessProfile }, getAccessProfile] = useGlobalAction(api.getCurrentUserAccessProfile);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [workflows, setWorkflows] = React.useState([]);
  const [availableInstitutions, setAvailableInstitutions] = React.useState([]);
  const [selectedInstitution, setSelectedInstitution] = React.useState("all");
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState("");
  const [reviewCharts, setReviewCharts] = React.useState([]);
  const [releasedCharts, setReleasedCharts] = React.useState([]);
  const [selectedPayload, setSelectedPayload] = React.useState(null);
  const [selectedChartId, setSelectedChartId] = React.useState("");
  const [selectedScope, setSelectedScope] = React.useState("review_queue");
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [detailError, setDetailError] = React.useState("");
  const [tabIndex, setTabIndex] = React.useState(0);
  const [responseDraft, setResponseDraft] = React.useState({ domain: "clinical", response: "", addressedHow: "" });

  const canReview = accessProfile?.agencyQa?.canAccessReviewPortal === true || accessProfile?.roles?.isGadgetAdmin === true;
  const canConfigure = accessProfile?.roles?.canConfigureAgencyQa === true || accessProfile?.roles?.isGadgetAdmin === true;
  const canAccessReleased = accessProfile?.agencyQa?.canAccessReleasedCharts === true || accessProfile?.roles?.isGadgetAdmin === true;
  const isGadgetAdmin = accessProfile?.roles?.isGadgetAdmin === true;
  const selectedInstitutionLabel = selectedInstitution === "all"
    ? "All institutions"
    : selectedInstitution || accessProfile?.user?.institution || "Institution";

  const loadAccess = React.useCallback(async () => {
    if (!user?.id) return;
    await getAccessProfile({});
  }, [user?.id, getAccessProfile]);

  const loadConfig = React.useCallback(async () => {
    if (!canReview) return;
    const query = isGadgetAdmin && selectedInstitution !== "all"
      ? `?institution=${encodeURIComponent(selectedInstitution)}`
      : "";
    const response = await api.fetch(`/live-qa/config${query}`);
    const payload = await response.json();
    if (payload?.success === false) throw new Error(payload.error || "Failed to load Agency QA configuration");
    const nextWorkflows = payload.workflows || [];
    const nextInstitutions = payload.availableInstitutions || [];
    const nextWorkflowId = selectedWorkflowId && nextWorkflows.some((entry) => entry.id === selectedWorkflowId)
      ? selectedWorkflowId
      : nextWorkflows[0]?.id || "";
    setAvailableInstitutions(nextInstitutions);
    setWorkflows(nextWorkflows);
    setSelectedWorkflowId(nextWorkflowId);
  }, [canReview, isGadgetAdmin, selectedInstitution, selectedWorkflowId]);

  const loadReviewCharts = React.useCallback(async (workflowId) => {
    if (!workflowId || !canReview) return;
    const institutionQuery = isGadgetAdmin && selectedInstitution !== "all"
      ? `&institution=${encodeURIComponent(selectedInstitution)}`
      : "";
    const response = await api.fetch(`/live-qa/charts?workflowId=${encodeURIComponent(workflowId)}&scope=review_queue${institutionQuery}`);
    const payload = await response.json();
    if (payload?.success === false) throw new Error(payload.error || "Failed to load review charts");
    setReviewCharts(payload.charts || []);
  }, [canReview, isGadgetAdmin, selectedInstitution]);

  const loadReleasedCharts = React.useCallback(async () => {
    if (!canAccessReleased) return;
    const query = isGadgetAdmin && selectedInstitution !== "all"
      ? `?scope=released_to_me&institution=${encodeURIComponent(selectedInstitution)}`
      : "?scope=released_to_me";
    const response = await api.fetch(`/live-qa/charts${query}`);
    const payload = await response.json();
    if (payload?.success === false) throw new Error(payload.error || "Failed to load released charts");
    setReleasedCharts(payload.charts || []);
  }, [canAccessReleased, isGadgetAdmin, selectedInstitution]);

  const loadChartDetail = React.useCallback(async (chartId, scope = "review_queue", chartPreview = null) => {
    const resolvedChartId = String(chartId || chartPreview?.id || "").trim();
    if (!resolvedChartId) {
      setError("Chart detail could not be loaded because the selected chart is missing an ID.");
      return;
    }
    setSelectedChartId(resolvedChartId);
    setSelectedScope(scope);
    if (chartPreview) {
      setSelectedPayload((current) => ({
        ...(current || {}),
        chart: chartPreview,
        fields: [],
        reviewDecisions: [],
        previewOnly: true,
      }));
    }
    setLoadingDetail(true);
    setError("");
    setDetailError("");
    try {
      const detailQuery = new URLSearchParams({ scope });
      const workflowId = chartPreview?.workflow?.id || chartPreview?.workflowId || selectedWorkflowId || "";
      if (workflowId) {
        detailQuery.set("workflowId", workflowId);
      }
      const response = await api.fetch(`/live-qa/charts/${encodeURIComponent(resolvedChartId)}?${detailQuery.toString()}`);
      const payload = await response.json();
      if (payload?.success === false) throw new Error(payload.error || "Failed to load chart detail");
      setSelectedPayload(payload);
      const firstDomain = payload?.chart?.domains?.[0] || "clinical";
      setResponseDraft((current) => ({ ...current, domain: firstDomain }));
    } catch (detailError) {
      const message = detailError.message || "Failed to load chart detail";
      setError(message);
      setDetailError(message);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedWorkflowId]);

  React.useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  React.useEffect(() => {
    let cancelled = false;
    if (!accessProfile) return;
    (async () => {
      setLoading(true);
      setError("");
      try {
        if (canReview) await loadConfig();
        if (canAccessReleased) await loadReleasedCharts();
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "Failed to load Agency QA");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessProfile, canReview, canAccessReleased, loadConfig, loadReleasedCharts]);

  React.useEffect(() => {
    if (!selectedWorkflowId) return;
    loadReviewCharts(selectedWorkflowId).catch((loadError) => setError(loadError.message || "Failed to load charts"));
  }, [selectedWorkflowId, loadReviewCharts]);

  const refreshCurrent = async () => {
    if (selectedWorkflowId) await loadReviewCharts(selectedWorkflowId);
    await loadReleasedCharts();
    if (selectedPayload?.chart?.id) {
      await loadChartDetail(selectedPayload.chart.id, selectedScope);
    }
  };

  const linkCrew = async ({ chartId, row, candidate, persistAlias }) => {
    if (persistAlias) {
      const confirmed = window.confirm("Remember this presented crew identity for future charts from this institution?");
      if (!confirmed) return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/crew-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chartId,
          crewIndex: row.index,
          crewName: row.name,
          externalId: row.externalId,
          userId: candidate.id,
          persistAlias,
        }),
      });
      const payload = await response.json();
      if (payload?.success === false) throw new Error(payload.error || "Failed to link crew");
      await refreshCurrent();
    } catch (linkError) {
      setError(linkError.message || "Failed to link crew");
    } finally {
      setSaving(false);
    }
  };

  const retryLink = async (chartId) => {
    setSaving(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/relink-crew", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chartId }),
      });
      const payload = await response.json();
      if (payload?.success === false) throw new Error(payload.error || "Failed to retry crew matching");
      await refreshCurrent();
    } catch (retryError) {
      setError(retryError.message || "Failed to retry crew matching");
    } finally {
      setSaving(false);
    }
  };

  const submitResponse = async (chartId) => {
    setSaving(true);
    setError("");
    try {
      const response = await api.fetch("/live-qa/clinician-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chartId, ...responseDraft }),
      });
      const payload = await response.json();
      if (payload?.success === false) throw new Error(payload.error || "Failed to submit response");
      setResponseDraft((current) => ({ ...current, response: "", addressedHow: "" }));
      await refreshCurrent();
    } catch (responseError) {
      setError(responseError.message || "Failed to submit response");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !accessProfile) {
    return (
      <Flex minH="50vh" align="center" justify="center" bg={pageBg} color="gray.100">
        <Spinner />
      </Flex>
    );
  }

  if (!canReview && !canAccessReleased) {
    return (
      <Box minH="100vh" bg={pageBg} color="gray.100" p={6}>
        <Alert status="warning" borderRadius="md" bg="#3a2a10" color="orange.100" borderWidth="1px" borderColor="orange.700">
          <AlertIcon />
          <AlertDescription>Agency QA is not available for this account.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh">
    <Box maxW="1440px" mx="auto" p={{ base: 4, md: 6 }} color="gray.100">
      <Stack spacing={5}>
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={3} direction={{ base: "column", md: "row" }}>
          <Box>
            <Button
              as={Link}
              to="/index"
              leftIcon={<ArrowBackIcon />}
              size="sm"
              variant="ghost"
              colorScheme="cyan"
              mb={3}
              px={2}
              _hover={{ bg: "whiteAlpha.100" }}
            >
              Back to Home
            </Button>
            <Heading size="lg">Agency QA</Heading>
            <Text color={textMuted}>{selectedInstitutionLabel} chart review and released reports</Text>
          </Box>
          <HStack spacing={3} align="center" wrap="wrap">
            {isGadgetAdmin && (
              <Select
                {...darkControlProps}
                minW={{ base: "100%", md: "280px" }}
                value={selectedInstitution}
                onChange={(event) => {
                  setSelectedInstitution(event.target.value);
                  setSelectedWorkflowId("");
                  setSelectedChartId("");
                  setSelectedPayload(null);
                  setDetailError("");
                  setReviewCharts([]);
                  setReleasedCharts([]);
                }}
              >
                <option value="all">All institutions</option>
                {availableInstitutions.map((institution) => (
                  <option key={institution} value={institution}>{institution}</option>
                ))}
              </Select>
            )}
            {canConfigure && (
              <Button as={Link} to="/admin/live-qa" rightIcon={<ExternalLinkIcon />} variant="outline" colorScheme="cyan">
                Configuration
              </Button>
            )}
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" bg="#3f1515" color="red.100" borderWidth="1px" borderColor="red.700">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs index={tabIndex} onChange={setTabIndex} variant="soft-rounded" colorScheme="cyan">
          <TabList bg={panelBg} borderWidth="1px" borderColor={borderColor} borderRadius="full" p={1} w="fit-content">
            {canReview && <Tab>Review Queue</Tab>}
            {canAccessReleased && <Tab>Released To Me</Tab>}
          </TabList>
          <TabPanels>
            {canReview && (
              <TabPanel px={0}>
                <Stack spacing={4}>
                  <Flex justify="space-between" align="center" gap={3}>
                    <Select {...darkControlProps} maxW="520px" value={selectedWorkflowId} onChange={(event) => setSelectedWorkflowId(event.target.value)}>
                      {workflows.map((workflow) => (
                        <option key={workflow.id} value={workflow.id}>{isGadgetAdmin ? `${workflow.institution} - ${workflow.name}` : workflow.name}</option>
                      ))}
                    </Select>
                    <Button leftIcon={<RepeatIcon />} onClick={() => loadReviewCharts(selectedWorkflowId)}>
                      Refresh
                    </Button>
                  </Flex>
                  <Grid templateColumns={{ base: "1fr", lg: "390px 1fr" }} gap={5}>
                    <ChartList
                      charts={reviewCharts}
                      selectedChartId={selectedChartId || selectedPayload?.chart?.id}
                      onSelect={(chart) => loadChartDetail(chart.id, "review_queue", chart)}
                    />
                    <ChartDetail
                      payload={selectedPayload}
                      canLinkCrew={true}
                      releasedView={false}
                      onLinkCrew={linkCrew}
                      onRetryLink={retryLink}
                      onSubmitResponse={submitResponse}
                      responseDraft={responseDraft}
                      setResponseDraft={setResponseDraft}
                      saving={saving}
                      loadingDetail={loadingDetail}
                      detailError={detailError}
                    />
                  </Grid>
                </Stack>
              </TabPanel>
            )}
            {canAccessReleased && (
              <TabPanel px={0}>
                <Stack spacing={4}>
                  <Flex justify="space-between" align="center">
                    <Heading size="sm">Released Reports</Heading>
                    <Button leftIcon={<RepeatIcon />} onClick={loadReleasedCharts}>Refresh</Button>
                  </Flex>
                  <Grid templateColumns={{ base: "1fr", lg: "390px 1fr" }} gap={5}>
                    <ChartList
                      charts={releasedCharts}
                      selectedChartId={selectedChartId || selectedPayload?.chart?.id}
                      onSelect={(chart) => loadChartDetail(chart.id, "released_to_me", chart)}
                    />
                    <ChartDetail
                      payload={selectedPayload}
                      canLinkCrew={false}
                      releasedView={true}
                      onLinkCrew={linkCrew}
                      onRetryLink={retryLink}
                      onSubmitResponse={submitResponse}
                      responseDraft={responseDraft}
                      setResponseDraft={setResponseDraft}
                      saving={saving}
                      loadingDetail={loadingDetail}
                      detailError={detailError}
                    />
                  </Grid>
                </Stack>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>

        <Divider />
      </Stack>
    </Box>
    </Box>
  );
}
